/**
 * CreatePostModal Component
 *
 * Modal for creating new posts/drafts with two options:
 * 1. From URL (automatically detects source type)
 * 2. Manual text entry
 * Enhanced with better visual hierarchy and clearer action buttons.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
  Alert,
  useMediaQuery,
  LinearProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// API
import { fetchTrendingTopics } from "api/openai";

function CreatePostModal({ open, onClose, onSubmit, loading, error }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0); // 0 = URL, 1 = Manual, 2 = Trending
  const [url, setUrl] = useState("");
  const [manualTopic, setManualTopic] = useState("");
  const [manualContent, setManualContent] = useState("");
  const [validationError, setValidationError] = useState("");

  // Trending tab state
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [selectedTrending, setSelectedTrending] = useState(null);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [trendingError, setTrendingError] = useState("");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setValidationError("");
  };

  const handleUrlChange = (event) => {
    setUrl(event.target.value);
    setValidationError("");
  };

  const handleManualTopicChange = (event) => {
    setManualTopic(event.target.value);
    setValidationError("");
  };

  const handleManualContentChange = (event) => {
    setManualContent(event.target.value);
    setValidationError("");
  };

  const handleFetchTrending = async () => {
    setTrendingLoading(true);
    setTrendingError("");
    setSelectedTrending(null);
    try {
      const topics = await fetchTrendingTopics();
      setTrendingTopics(topics);
    } catch (err) {
      setTrendingError(err.message || "Failed to fetch trending topics");
    } finally {
      setTrendingLoading(false);
    }
  };

  const handleSelectTrending = (topic) => {
    setSelectedTrending(topic);
    setValidationError("");
  };

  const validateUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSubmit = () => {
    setValidationError("");

    if (activeTab === 0) {
      // URL submission
      if (!url.trim()) {
        setValidationError("Please enter a URL");
        return;
      }
      if (!validateUrl(url.trim())) {
        setValidationError("Please enter a valid URL");
        return;
      }

      onSubmit({
        type: "url",
        url: url.trim(),
      });
    } else if (activeTab === 1) {
      // Manual submission
      if (!manualTopic.trim()) {
        setValidationError("Please enter a topic");
        return;
      }
      if (!manualContent.trim()) {
        setValidationError("Please enter content or article notes");
        return;
      }

      onSubmit({
        type: "manual",
        topic: manualTopic.trim(),
        content: manualContent.trim(),
      });
    } else if (activeTab === 2) {
      // Trending submission
      if (!selectedTrending) {
        setValidationError("Please select a trending topic");
        return;
      }

      // Sanitize strings to avoid JSON parsing issues in n8n
      // Replace double quotes with single quotes, remove newlines
      const sanitize = (str) => str.replace(/"/g, "'").replace(/\n/g, " ").replace(/\r/g, "");

      // Combine content without newlines to avoid JSON parsing issues in n8n
      const trendingContent = [
        selectedTrending.description,
        selectedTrending.context,
        `Source: ${selectedTrending.source_hint}`,
      ].join(" | ");

      onSubmit({
        type: "manual",
        topic: sanitize(selectedTrending.title),
        content: sanitize(trendingContent),
      });
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setActiveTab(0);
      setUrl("");
      setManualTopic("");
      setManualContent("");
      setValidationError("");
      // Reset trending state
      setTrendingTopics([]);
      setSelectedTrending(null);
      setTrendingError("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          maxHeight: isMobile ? "100%" : "90vh",
          overflow: "hidden",
        },
      }}
    >
      {/* Gradient Header */}
      <MDBox
        sx={{
          background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
          pt: isMobile ? "calc(env(safe-area-inset-top) + 16px)" : 2.5,
          pb: 2.5,
          px: { xs: 2, sm: 3 },
          position: "relative",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            top: isMobile ? "calc(env(safe-area-inset-top) + 8px)" : 8,
            right: 8,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            minWidth: "44px",
            minHeight: "44px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.25)",
            },
          }}
        >
          <Icon>close</Icon>
        </IconButton>

        <MDBox display="flex" alignItems="center" gap={1.5}>
          <MDBox
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: "white", fontSize: "1.5rem !important" }}>auto_awesome</Icon>
          </MDBox>
          <MDBox>
            <MDTypography variant="h5" sx={{ color: "#FFFFFF !important", fontWeight: 700 }}>
              Create New Post
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.8) !important" }}>
              AI will generate a complete blog post with social media versions
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Loading Progress */}
      {loading && (
        <LinearProgress
          sx={{
            height: "3px",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#1A73E8",
            },
          }}
        />
      )}

      <DialogContent sx={{ pt: 0, pb: isMobile ? "calc(env(safe-area-inset-bottom) + 16px)" : 3 }}>
        {/* Tab Navigation */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            mt: 2,
            mb: 3,
            borderBottom: "1px solid",
            borderColor: "rgba(0,0,0,0.08)",
            "& .MuiTab-root": {
              minHeight: { xs: "52px", sm: "48px" },
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
              textTransform: "none",
              fontWeight: 500,
              color: "#6B7280",
              "&.Mui-selected": {
                color: "#1A73E8",
                fontWeight: 600,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#1A73E8",
              height: "3px",
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            label={
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ fontSize: "1.1rem !important" }}>link</Icon>
                <span>From URL</span>
              </MDBox>
            }
            disabled={loading}
          />
          <Tab
            label={
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ fontSize: "1.1rem !important" }}>edit_note</Icon>
                <span>Manual Entry</span>
              </MDBox>
            }
            disabled={loading}
          />
          <Tab
            label={
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ fontSize: "1.1rem !important" }}>trending_up</Icon>
                <span>Trending</span>
              </MDBox>
            }
            disabled={loading}
          />
        </Tabs>

        {/* Error Display */}
        {(validationError || error || trendingError) && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: "12px",
              "& .MuiAlert-icon": {
                alignItems: "center",
              },
            }}
          >
            {validationError || error || trendingError}
          </Alert>
        )}

        {/* URL Tab */}
        {activeTab === 0 && (
          <MDBox>
            <MDBox
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: "12px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E2E8F0",
              }}
            >
              <MDTypography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                Paste a URL from a <strong>webpage</strong>, <strong>X (Twitter)</strong>,{" "}
                <strong>LinkedIn</strong>, or <strong>Reddit</strong> post. The source will be
                automatically detected and analyzed by AI.
              </MDTypography>
            </MDBox>

            {/* URL Input */}
            <TextField
              fullWidth
              label="Source URL"
              placeholder="https://example.com/article or social media post URL"
              value={url}
              onChange={handleUrlChange}
              disabled={loading}
              autoFocus
              InputProps={{
                startAdornment: <Icon sx={{ mr: 1, color: "text.secondary" }}>link</Icon>,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1A73E8",
                    borderWidth: "2px",
                  },
                },
              }}
            />

            {/* Supported Sources */}
            <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
              <MDTypography variant="caption" sx={{ color: "#94A3B8" }}>
                Supported:
              </MDTypography>
              {["Webpages", "X/Twitter", "LinkedIn", "Reddit"].map((source) => (
                <MDBox
                  key={source}
                  sx={{
                    px: 1.5,
                    py: 0.25,
                    borderRadius: "6px",
                    backgroundColor: "rgba(26, 115, 232, 0.08)",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    color: "#1A73E8",
                  }}
                >
                  {source}
                </MDBox>
              ))}
            </MDBox>
          </MDBox>
        )}

        {/* Manual Entry Tab */}
        {activeTab === 1 && (
          <MDBox>
            <MDBox
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: "12px",
                backgroundColor: "#FFF8F0",
                border: "1px solid #FFEDD5",
              }}
            >
              <MDTypography variant="body2" sx={{ color: "#9A3412", lineHeight: 1.6 }}>
                Describe your topic and provide key points or article notes. AI will generate a
                complete blog post with matching social media content.
              </MDTypography>
            </MDBox>

            {/* Topic Input */}
            <TextField
              fullWidth
              label="Topic / Title"
              placeholder="e.g., The Future of AI in Healthcare"
              value={manualTopic}
              onChange={handleManualTopicChange}
              disabled={loading}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FB8C00",
                    borderWidth: "2px",
                  },
                },
              }}
              InputProps={{
                startAdornment: <Icon sx={{ mr: 1, color: "text.secondary" }}>title</Icon>,
              }}
            />

            {/* Content/Article Input */}
            <TextField
              fullWidth
              multiline
              rows={8}
              label="Content / Article Notes"
              placeholder="Enter the main content, key points, or article notes you want to turn into a post..."
              value={manualContent}
              onChange={handleManualContentChange}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FB8C00",
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <MDTypography variant="caption" sx={{ color: "#94A3B8", mt: 1, display: "block" }}>
              {manualContent.length} characters
            </MDTypography>
          </MDBox>
        )}

        {/* Trending Tab */}
        {activeTab === 2 && (
          <MDBox>
            <MDBox
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: "12px",
                backgroundColor: "#F0FDF4",
                border: "1px solid #BBF7D0",
              }}
            >
              <MDTypography variant="body2" sx={{ color: "#166534 !important", lineHeight: 1.6 }}>
                Discover <strong>trending topics</strong> and <strong>breaking news</strong> in AI
                and tech. AI will find the most relevant stories and help you create timely content.
              </MDTypography>
            </MDBox>

            {/* Fetch Trending Button */}
            {trendingTopics.length === 0 && !trendingLoading && (
              <MDBox display="flex" justifyContent="center" py={4}>
                <MDButton
                  onClick={handleFetchTrending}
                  disabled={loading}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "1rem",
                    boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      boxShadow: "0 6px 20px rgba(16, 185, 129, 0.5)",
                    },
                  }}
                >
                  <Icon sx={{ mr: 1, fontSize: "1.3rem !important" }}>search</Icon>
                  Find Trending Topics
                </MDButton>
              </MDBox>
            )}

            {/* Loading State */}
            {trendingLoading && (
              <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
                <CircularProgress sx={{ color: "#10B981" }} />
                <MDTypography variant="body2" sx={{ color: "#6B7280" }}>
                  Searching for trending topics...
                </MDTypography>
              </MDBox>
            )}

            {/* Trending Topics List */}
            {trendingTopics.length > 0 && !trendingLoading && (
              <MDBox>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography
                    variant="subtitle2"
                    sx={{ color: "#374151 !important", fontWeight: 600 }}
                  >
                    Select a topic to create content:
                  </MDTypography>
                  <MDButton
                    onClick={handleFetchTrending}
                    size="small"
                    sx={{
                      textTransform: "none",
                      color: "#10B981",
                      "&:hover": { backgroundColor: "rgba(16, 185, 129, 0.08)" },
                    }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: "1rem !important" }}>refresh</Icon>
                    Refresh
                  </MDButton>
                </MDBox>

                <MDBox display="flex" flexDirection="column" gap={1.5}>
                  {trendingTopics.map((topic, index) => (
                    <MDBox
                      key={index}
                      onClick={() => handleSelectTrending(topic)}
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        border: "2px solid",
                        borderColor: selectedTrending === topic ? "#10B981" : "rgba(0, 0, 0, 0.08)",
                        backgroundColor:
                          selectedTrending === topic ? "rgba(16, 185, 129, 0.05)" : "#FAFAFA",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor:
                            selectedTrending === topic ? "#10B981" : "rgba(16, 185, 129, 0.4)",
                          backgroundColor:
                            selectedTrending === topic
                              ? "rgba(16, 185, 129, 0.05)"
                              : "rgba(16, 185, 129, 0.02)",
                        },
                      }}
                    >
                      <MDBox display="flex" alignItems="flex-start" gap={1.5}>
                        <MDBox
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: "2px solid",
                            borderColor:
                              selectedTrending === topic ? "#10B981" : "rgba(0, 0, 0, 0.2)",
                            backgroundColor: selectedTrending === topic ? "#10B981" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        >
                          {selectedTrending === topic && (
                            <Icon sx={{ color: "white", fontSize: "0.9rem !important" }}>
                              check
                            </Icon>
                          )}
                        </MDBox>
                        <MDBox flex={1}>
                          <MDTypography
                            variant="subtitle2"
                            sx={{
                              color: "#1F2937 !important",
                              fontWeight: 600,
                              mb: 0.5,
                              lineHeight: 1.4,
                            }}
                          >
                            {topic.title}
                          </MDTypography>
                          <MDTypography
                            variant="body2"
                            sx={{
                              color: "#6B7280 !important",
                              fontSize: "0.85rem",
                              lineHeight: 1.5,
                            }}
                          >
                            {topic.description}
                          </MDTypography>
                          <MDBox
                            sx={{
                              mt: 1,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "6px",
                              backgroundColor: "rgba(107, 114, 128, 0.1)",
                              display: "inline-block",
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{ color: "#6B7280", fontSize: "0.7rem" }}
                            >
                              {topic.source_hint}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        )}

        {/* Action Buttons - Sticky Footer */}
        <MDBox
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            pt: 3,
            pb: 1,
            mt: 2,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <MDBox display="flex" gap={1.5} flexDirection={{ xs: "column", sm: "row" }}>
            <MDButton
              onClick={handleClose}
              disabled={loading}
              fullWidth={isMobile}
              sx={{
                minHeight: { xs: "48px", sm: "48px" },
                borderRadius: "12px",
                backgroundColor: "#F3F4F6",
                color: "#374151",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                flex: { xs: "auto", sm: "0 0 auto" },
                order: { xs: 1, sm: 0 },
                "&:hover": {
                  backgroundColor: "#E5E7EB",
                  boxShadow: "none",
                },
              }}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleSubmit}
              disabled={
                loading ||
                trendingLoading ||
                (activeTab === 0
                  ? !url.trim()
                  : activeTab === 1
                  ? !manualTopic.trim() || !manualContent.trim()
                  : !selectedTrending)
              }
              fullWidth={isMobile}
              sx={{
                flex: 1,
                minHeight: { xs: "52px", sm: "48px" },
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.95rem",
                background: loading
                  ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
                  : "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                color: "#FFFFFF !important",
                boxShadow: loading ? "none" : "0 4px 14px rgba(26, 115, 232, 0.4)",
                order: { xs: 0, sm: 1 },
                "& .MuiIcon-root": {
                  color: "#FFFFFF !important",
                },
                "&:hover": {
                  background: loading
                    ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
                    : "linear-gradient(135deg, #0D47A1 0%, #1A237E 100%)",
                  boxShadow: loading ? "none" : "0 6px 20px rgba(26, 115, 232, 0.5)",
                },
                "&.Mui-disabled": {
                  background: "linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)",
                  color: "#9CA3AF !important",
                },
              }}
            >
              {loading ? (
                <MDBox display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                  <span>Generating...</span>
                </MDBox>
              ) : (
                <MDBox display="flex" alignItems="center" gap={0.75}>
                  <Icon sx={{ fontSize: "1.2rem !important" }}>auto_awesome</Icon>
                  <span>Generate Draft</span>
                </MDBox>
              )}
            </MDButton>
          </MDBox>

          {/* Loading State Info */}
          {loading && (
            <MDBox
              mt={2}
              p={2}
              sx={{
                backgroundColor: "#EFF6FF",
                borderRadius: "12px",
                border: "1px solid #BFDBFE",
              }}
            >
              <MDBox display="flex" alignItems="flex-start" gap={1.5}>
                <Icon sx={{ color: "#3B82F6", fontSize: "1.2rem !important", mt: 0.25 }}>info</Icon>
                <MDBox>
                  <MDTypography variant="body2" sx={{ color: "#1E40AF", fontWeight: 600, mb: 0.5 }}>
                    AI is working on your content
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "#3B82F6" }}>
                    This typically takes 30-60 seconds. We&apos;re generating a blog post, social
                    media posts, and an image.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

CreatePostModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

CreatePostModal.defaultProps = {
  loading: false,
  error: null,
};

export default CreatePostModal;
